import { useState, useEffect, useCallback, useRef } from 'react';
import { getDemoDatabase, type DemoRow } from '../../models/demo';
import {
  deleteDemoRow,
  insertDemoRow,
  readNumber,
  readText,
  updateDemoRow,
  type DemoDb,
} from '../../models/demo/sql';
import type { ProductVariant } from '../../redux/productSlice';
import { DemoProductItem, getDemoProductById } from './useDemoProducts';
import {
  composeProductVariantLabel,
  getProductDisplayName,
} from '../../utils/productDisplayName';
import {
  getSellLimit,
  isServiceLine,
  type ProductKind,
} from '../../utils/productKind';

export interface DemoCartItemData {
  _id: string;
  product_id: string;
  variant_id?: string;
  quantity: number;
  mass: number;
  unit_price: number;
  price_type: 'item' | 'unit';
  kind: ProductKind;
  name: string;
  unit_of_mass: string;
  category: string;
  image: string;
  created_at: number;
  max_available: number;
}

export type DemoAddToCartOptions = {
  variantId?: string | null;
};

/** In-memory-only id until the database row exists. */
export const DEMO_PENDING_PREFIX = '__demo_pending__';

export function isPendingDemoCartId(id: string): boolean {
  return id.startsWith(DEMO_PENDING_PREFIX);
}

export function normalizeDemoVariantId(variantId?: string | null): string {
  return variantId ? String(variantId) : '';
}

export function demoCartIdentityKey(
  productId: string,
  variantId?: string | null,
): string {
  return `${productId}::${normalizeDemoVariantId(variantId)}`;
}

function pendingDemoCartId(
  productId: string,
  variantId?: string | null,
): string {
  return `${DEMO_PENDING_PREFIX}${demoCartIdentityKey(productId, variantId)}`;
}

function matchesCartIdentity(
  item: Pick<DemoCartItemData, 'product_id' | 'variant_id'>,
  productId: string,
  variantId?: string | null,
): boolean {
  return (
    item.product_id === productId &&
    normalizeDemoVariantId(item.variant_id) ===
      normalizeDemoVariantId(variantId)
  );
}

function findVariant(
  product: DemoProductItem,
  variantId?: string | null,
): ProductVariant | null {
  const id = normalizeDemoVariantId(variantId);
  if (!id) return null;
  return (
    product.bp_product_variants?.find(variant => variant._id === id) ?? null
  );
}

function resolveLinePricing(
  product: DemoProductItem,
  variantId?: string | null,
): {
  variantId: string;
  unitPrice: number;
  maxAvailable: number;
  name: string;
  image: string;
} {
  const variant = findVariant(product, variantId);
  if (variant) {
    const attrLabel =
      variant.bp_variant_display_name ||
      Object.values(variant.bp_variant_attributes || {})
        .filter(Boolean)
        .join(', ');
    const parentName = String(product.bp_product_name || '').trim();
    return {
      variantId: variant._id,
      unitPrice: Number(variant.bp_variant_selling_price ?? 0),
      maxAvailable: Number(variant.bp_variant_quantity ?? 0),
      name: composeProductVariantLabel(parentName, attrLabel),
      image: variant.bp_variant_image || product.bp_product_image,
    };
  }
  return {
    variantId: '',
    unitPrice: Number(product.bp_product_selling_price ?? 0),
    maxAvailable: getSellLimit(
      product.bp_product_kind,
      Number(product.bp_product_quantity ?? 0),
    ),
    name: getProductDisplayName(product),
    image: product.bp_product_image,
  };
}

async function resolveMaxAvailable(
  productId: string,
  variantId?: string | null,
): Promise<number> {
  const product = await getDemoProductById(productId);
  if (!product) return 0;
  return resolveLinePricing(product, variantId).maxAvailable;
}

function convertModelToCartItemData(
  model: DemoRow<'demo_cart_items'>,
  maxAvailable: number = 0,
): DemoCartItemData {
  const variantId = normalizeDemoVariantId(model.variant_id);
  return {
    _id: model.id,
    product_id: readText(model.product_id),
    variant_id: variantId || undefined,
    quantity: readNumber(model.quantity),
    mass: readNumber(model.mass),
    unit_price: readNumber(model.unit_price),
    price_type: readText(model.price_type) as 'item' | 'unit',
    kind: isServiceLine(model) ? 'service' : 'product',
    name: readText(model.name),
    unit_of_mass: readText(model.unit_of_mass),
    category: readText(model.category),
    image: readText(model.image),
    created_at: readNumber(model.created_at),
    max_available: maxAvailable,
  };
}

function buildPendingUnitItem(
  product: DemoProductItem,
  mass: number,
  variantId?: string | null,
): DemoCartItemData {
  const line = resolveLinePricing(product, variantId);
  return {
    _id: pendingDemoCartId(product._id, line.variantId),
    product_id: product._id,
    variant_id: line.variantId || undefined,
    quantity: 0,
    mass,
    unit_price: line.unitPrice,
    price_type: 'unit',
    kind: product.bp_product_kind,
    name: line.name,
    unit_of_mass: product.bp_product_unit_of_mass,
    category: product.bp_product_category,
    image: line.image,
    created_at: Date.now(),
    max_available: line.maxAvailable,
  };
}

function buildPendingItemRow(
  product: DemoProductItem,
  quantity: number,
  variantId?: string | null,
): DemoCartItemData {
  const line = resolveLinePricing(product, variantId);
  return {
    _id: pendingDemoCartId(product._id, line.variantId),
    product_id: product._id,
    variant_id: line.variantId || undefined,
    quantity,
    mass: 0,
    unit_price: line.unitPrice,
    price_type: 'item',
    kind: product.bp_product_kind,
    name: line.name,
    unit_of_mass: product.bp_product_unit_of_mass,
    category: product.bp_product_category,
    image: line.image,
    created_at: Date.now(),
    max_available: line.maxAvailable,
  };
}

function getCartRowsForIdentity(
  db: DemoDb,
  productId: string,
  variantId?: string | null,
): Promise<DemoRow<'demo_cart_items'>[]> {
  return db.getAll<DemoRow<'demo_cart_items'>>(
    'SELECT * FROM demo_cart_items WHERE product_id = ? AND variant_id = ?',
    [productId, normalizeDemoVariantId(variantId)],
  );
}

async function deleteCartRowsForIdentity(
  db: DemoDb,
  productId: string,
  variantId?: string | null,
): Promise<void> {
  await db.execute(
    'DELETE FROM demo_cart_items WHERE product_id = ? AND variant_id = ?',
    [productId, normalizeDemoVariantId(variantId)],
  );
}

const FLUSH_DEBOUNCE_MS = 200;

interface UseDemoCartResult {
  cartItems: DemoCartItemData[];
  isLoading: boolean;
  error: string | null;
  cartTotal: number;
  cartCount: number;
  addToCart: (
    product: DemoProductItem,
    quantity?: number,
    mass?: number,
    options?: DemoAddToCartOptions,
  ) => Promise<boolean>;
  removeFromCart: (cartItemId: string) => Promise<boolean>;
  updateCartItemQuantity: (
    cartItemId: string,
    quantity: number,
  ) => Promise<boolean>;
  updateCartItemMass: (cartItemId: string, mass: number) => Promise<boolean>;
  /** Instant UI + debounced DB; clamps to product/variant stock (a service has none). */
  setDemoCartUnitMass: (
    product: DemoProductItem,
    mass: number,
    options?: DemoAddToCartOptions,
  ) => void;
  /** Instant UI + debounced DB; clamps to product/variant stock (a service has none). */
  setDemoCartItemQuantity: (
    product: DemoProductItem,
    quantity: number,
    options?: DemoAddToCartOptions,
  ) => void;
  clearCart: () => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useDemoCart(): UseDemoCartResult {
  const [cartItems, setCartItems] = useState<DemoCartItemData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cartItemsRef = useRef<DemoCartItemData[]>([]);
  cartItemsRef.current = cartItems;

  const persistTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map(),
  );
  const flushQueueRef = useRef<Map<string, Promise<void>>>(new Map());

  const fetchCart = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const db = await getDemoDatabase();
      // Insertion order, as WatermelonDB returned it
      const items = await db.getAll<DemoRow<'demo_cart_items'>>(
        'SELECT * FROM demo_cart_items ORDER BY created_at',
      );

      const cartItemsWithStock = await Promise.all(
        items.map(async item => {
          const maxAvailable = await resolveMaxAvailable(
            readText(item.product_id),
            item.variant_id,
          );
          return convertModelToCartItemData(item, maxAvailable);
        }),
      );

      setCartItems(cartItemsWithStock);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load cart';
      setError(errorMessage);
      console.error('[useDemoCart] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const runFlush = useCallback(
    async (identityKey: string) => {
      const db = await getDemoDatabase();
      const item = cartItemsRef.current.find(
        i => demoCartIdentityKey(i.product_id, i.variant_id) === identityKey,
      );
      const productId = item?.product_id;
      const variantId = item?.variant_id;

      const deleteDbRowsForIdentity = async () => {
        if (!productId) return;
        await deleteCartRowsForIdentity(db, productId, variantId);
      };

      if (
        !item ||
        !productId ||
        (item.price_type === 'unit' && item.mass <= 0) ||
        (item.price_type === 'item' && item.quantity <= 0)
      ) {
        // Best-effort delete when item removed from memory: parse identity key
        if (!item) {
          const [pid, vid = ''] = identityKey.split('::');
          if (pid) {
            await deleteCartRowsForIdentity(db, pid, vid);
          }
          return;
        }
        await deleteDbRowsForIdentity();
        return;
      }

      try {
        if (item.price_type === 'unit') {
          if (isPendingDemoCartId(item._id)) {
            await db.writeTransaction(async tx => {
              await insertDemoRow(tx, 'demo_cart_items', {
                product_id: item.product_id,
                variant_id: normalizeDemoVariantId(item.variant_id),
                quantity: 0,
                mass: item.mass,
                unit_price: item.unit_price,
                price_type: item.price_type,
                kind: item.kind,
                name: item.name,
                unit_of_mass: item.unit_of_mass,
                category: item.category,
                image: item.image,
                created_at: item.created_at || Date.now(),
              });
            });
            const freshRows = await getCartRowsForIdentity(
              db,
              productId,
              variantId,
            );
            const model = freshRows[0];
            if (model) {
              setCartItems(prev => {
                const cur = prev.find(i =>
                  matchesCartIdentity(i, productId, variantId),
                );
                if (!cur) return prev;
                return prev.map(i =>
                  matchesCartIdentity(i, productId, variantId)
                    ? {
                        ...convertModelToCartItemData(model, cur.max_available),
                        mass: cur.mass,
                        quantity: cur.quantity,
                      }
                    : i,
                );
              });
            }
          } else {
            await db.writeTransaction(async tx => {
              await updateDemoRow(tx, 'demo_cart_items', item._id, {
                mass: item.mass,
              });
            });
          }
        } else if (isPendingDemoCartId(item._id)) {
          await db.writeTransaction(async tx => {
            await insertDemoRow(tx, 'demo_cart_items', {
              product_id: item.product_id,
              variant_id: normalizeDemoVariantId(item.variant_id),
              quantity: item.quantity,
              mass: 0,
              unit_price: item.unit_price,
              price_type: item.price_type,
              kind: item.kind,
              name: item.name,
              unit_of_mass: item.unit_of_mass,
              category: item.category,
              image: item.image,
              created_at: item.created_at || Date.now(),
            });
          });
          const freshRows = await getCartRowsForIdentity(
            db,
            productId,
            variantId,
          );
          const model = freshRows[0];
          if (model) {
            setCartItems(prev => {
              const cur = prev.find(i =>
                matchesCartIdentity(i, productId, variantId),
              );
              if (!cur) return prev;
              return prev.map(i =>
                matchesCartIdentity(i, productId, variantId)
                  ? {
                      ...convertModelToCartItemData(model, cur.max_available),
                      mass: cur.mass,
                      quantity: cur.quantity,
                    }
                  : i,
              );
            });
          }
        } else {
          await db.writeTransaction(async tx => {
            await updateDemoRow(tx, 'demo_cart_items', item._id, {
              quantity: item.quantity,
            });
          });
        }
      } catch (err) {
        console.error('[useDemoCart] flush Error:', err);
        await fetchCart();
      }
    },
    [fetchCart],
  );

  const scheduleFlush = useCallback(
    (productId: string, variantId?: string | null) => {
      const identityKey = demoCartIdentityKey(productId, variantId);
      const timers = persistTimersRef.current;
      const existingTimer = timers.get(identityKey);
      if (existingTimer) clearTimeout(existingTimer);
      const t = setTimeout(() => {
        timers.delete(identityKey);
        const prev =
          flushQueueRef.current.get(identityKey) ?? Promise.resolve();
        const job = prev.catch(() => {}).then(() => runFlush(identityKey));
        flushQueueRef.current.set(identityKey, job);
      }, FLUSH_DEBOUNCE_MS);
      timers.set(identityKey, t);
    },
    [runFlush],
  );

  useEffect(
    () => () => {
      persistTimersRef.current.forEach(clearTimeout);
      persistTimersRef.current.clear();
    },
    [],
  );

  const setDemoCartUnitMass = useCallback(
    (
      product: DemoProductItem,
      mass: number,
      options?: DemoAddToCartOptions,
    ) => {
      if (product.bp_product_price_type !== 'unit') return;
      const line = resolveLinePricing(product, options?.variantId);
      const variantId = line.variantId;
      const safeMass = Number.isFinite(mass) ? Math.max(0, mass) : 0;
      const clamped = Math.min(safeMass, line.maxAvailable);

      setCartItems(prev => {
        const idx = prev.findIndex(i =>
          matchesCartIdentity(i, product._id, variantId),
        );
        if (clamped <= 0) {
          if (idx < 0) return prev;
          return prev.filter((_, i) => i !== idx);
        }
        if (idx < 0) {
          return [...prev, buildPendingUnitItem(product, clamped, variantId)];
        }
        const cur = prev[idx]!;
        if (cur.price_type !== 'unit') return prev;
        return prev.map((it, i) => (i === idx ? { ...it, mass: clamped } : it));
      });
      scheduleFlush(product._id, variantId);
    },
    [scheduleFlush],
  );

  const setDemoCartItemQuantity = useCallback(
    (
      product: DemoProductItem,
      quantity: number,
      options?: DemoAddToCartOptions,
    ) => {
      if (product.bp_product_price_type !== 'item') return;
      const line = resolveLinePricing(product, options?.variantId);
      const variantId = line.variantId;
      const safeQ = Math.max(0, Math.floor(quantity));
      const clamped = Math.min(safeQ, line.maxAvailable);

      setCartItems(prev => {
        const idx = prev.findIndex(i =>
          matchesCartIdentity(i, product._id, variantId),
        );
        if (clamped <= 0) {
          if (idx < 0) return prev;
          return prev.filter((_, i) => i !== idx);
        }
        if (idx < 0) {
          return [...prev, buildPendingItemRow(product, clamped, variantId)];
        }
        const cur = prev[idx]!;
        if (cur.price_type !== 'item') return prev;
        return prev.map((it, i) =>
          i === idx ? { ...it, quantity: clamped } : it,
        );
      });
      scheduleFlush(product._id, variantId);
    },
    [scheduleFlush],
  );

  const addToCart = useCallback(
    async (
      product: DemoProductItem,
      quantity: number = 1,
      mass: number = 0,
      options?: DemoAddToCartOptions,
    ): Promise<boolean> => {
      try {
        const variantId = options?.variantId;
        const line = resolveLinePricing(product, variantId);

        const inMem = cartItemsRef.current.find(i =>
          matchesCartIdentity(i, product._id, line.variantId),
        );
        if (inMem) {
          setCartItems(prev =>
            prev.map(i => {
              if (!matchesCartIdentity(i, product._id, line.variantId)) {
                return i;
              }
              if (product.bp_product_price_type === 'unit') {
                return { ...i, mass: i.mass + mass };
              }
              return { ...i, quantity: i.quantity + quantity };
            }),
          );
          scheduleFlush(product._id, line.variantId);
          return true;
        }

        const db = await getDemoDatabase();
        const existingItems = await getCartRowsForIdentity(
          db,
          product._id,
          line.variantId,
        );

        await db.writeTransaction(async tx => {
          if (existingItems.length > 0) {
            const existingItem = existingItems[0];
            await updateDemoRow(
              tx,
              'demo_cart_items',
              existingItem.id,
              product.bp_product_price_type === 'unit'
                ? { mass: readNumber(existingItem.mass) + mass }
                : { quantity: readNumber(existingItem.quantity) + quantity },
            );
          } else {
            await insertDemoRow(tx, 'demo_cart_items', {
              product_id: product._id,
              variant_id: line.variantId,
              quantity: product.bp_product_price_type === 'item' ? quantity : 0,
              mass: product.bp_product_price_type === 'unit' ? mass : 0,
              unit_price: line.unitPrice,
              price_type: product.bp_product_price_type,
              kind: product.bp_product_kind,
              name: line.name,
              unit_of_mass: product.bp_product_unit_of_mass,
              category: product.bp_product_category,
              image: line.image,
              created_at: Date.now(),
            });
          }
        });

        const fresh = await getCartRowsForIdentity(
          db,
          product._id,
          line.variantId,
        );
        if (fresh[0]) {
          const converted = convertModelToCartItemData(
            fresh[0],
            line.maxAvailable,
          );
          setCartItems(prev => {
            const has = prev.some(i =>
              matchesCartIdentity(i, product._id, line.variantId),
            );
            if (has) {
              return prev.map(i =>
                matchesCartIdentity(i, product._id, line.variantId)
                  ? converted
                  : i,
              );
            }
            return [...prev, converted];
          });
        }
        return true;
      } catch (err) {
        console.error('[useDemoCart] addToCart Error:', err);
        await fetchCart();
        return false;
      }
    },
    [fetchCart, scheduleFlush],
  );

  const removeFromCart = useCallback(
    async (cartItemId: string): Promise<boolean> => {
      try {
        const row = cartItemsRef.current.find(i => i._id === cartItemId);
        const productId = row?.product_id;
        const variantId = row?.variant_id;
        const identityKey =
          productId != null ? demoCartIdentityKey(productId, variantId) : null;

        if (isPendingDemoCartId(cartItemId) && identityKey) {
          const t = persistTimersRef.current.get(identityKey);
          if (t) clearTimeout(t);
          persistTimersRef.current.delete(identityKey);
          setCartItems(prev => prev.filter(i => i._id !== cartItemId));
          return true;
        }

        setCartItems(prev => prev.filter(i => i._id !== cartItemId));

        const db = await getDemoDatabase();
        await db.writeTransaction(async tx => {
          await deleteDemoRow(tx, 'demo_cart_items', cartItemId);
        });

        return true;
      } catch (err) {
        console.error('[useDemoCart] removeFromCart Error:', err);
        await fetchCart();
        return false;
      }
    },
    [fetchCart],
  );

  const updateCartItemQuantity = useCallback(
    async (cartItemId: string, quantity: number): Promise<boolean> => {
      try {
        const row = cartItemsRef.current.find(i => i._id === cartItemId);
        const productId = row?.product_id;
        if (!productId) {
          await fetchCart();
          return false;
        }

        if (quantity <= 0) {
          return removeFromCart(cartItemId);
        }

        setCartItems(prev =>
          prev.map(i => (i._id === cartItemId ? { ...i, quantity } : i)),
        );
        scheduleFlush(productId, row?.variant_id);
        return true;
      } catch (err) {
        console.error('[useDemoCart] updateCartItemQuantity Error:', err);
        await fetchCart();
        return false;
      }
    },
    [fetchCart, removeFromCart, scheduleFlush],
  );

  const updateCartItemMass = useCallback(
    async (cartItemId: string, mass: number): Promise<boolean> => {
      try {
        const row = cartItemsRef.current.find(i => i._id === cartItemId);
        const productId = row?.product_id;
        if (!productId) {
          await fetchCart();
          return false;
        }

        if (mass <= 0) {
          return removeFromCart(cartItemId);
        }

        setCartItems(prev =>
          prev.map(i => (i._id === cartItemId ? { ...i, mass } : i)),
        );
        scheduleFlush(productId, row?.variant_id);
        return true;
      } catch (err) {
        console.error('[useDemoCart] updateCartItemMass Error:', err);
        await fetchCart();
        return false;
      }
    },
    [fetchCart, removeFromCart, scheduleFlush],
  );

  const clearCart = useCallback(async (): Promise<boolean> => {
    try {
      persistTimersRef.current.forEach(clearTimeout);
      persistTimersRef.current.clear();
      flushQueueRef.current.clear();

      const db = await getDemoDatabase();
      await db.execute('DELETE FROM demo_cart_items');

      setCartItems([]);
      return true;
    } catch (err) {
      console.error('[useDemoCart] clearCart Error:', err);
      return false;
    }
  }, []);

  const cartTotal = cartItems.reduce((total, item) => {
    if (item.price_type === 'unit') {
      return total + item.unit_price * item.mass;
    }
    return total + item.unit_price * item.quantity;
  }, 0);

  const cartCount = cartItems.reduce((count, item) => {
    if (item.price_type === 'unit') {
      return count + 1;
    }
    return count + item.quantity;
  }, 0);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  return {
    cartItems,
    isLoading,
    error,
    cartTotal,
    cartCount,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    updateCartItemMass,
    setDemoCartUnitMass,
    setDemoCartItemQuantity,
    clearCart,
    refetch: fetchCart,
  };
}

export async function isProductInDemoCart(
  productId: string,
  variantId?: string | null,
): Promise<boolean> {
  try {
    const items = await getCartRowsForIdentity(
      await getDemoDatabase(),
      productId,
      variantId,
    );
    return items.length > 0;
  } catch (err) {
    console.error('[isProductInDemoCart] Error:', err);
    return false;
  }
}

export async function getDemoCartItemByProductId(
  productId: string,
  variantId?: string | null,
): Promise<DemoCartItemData | null> {
  try {
    const items = await getCartRowsForIdentity(
      await getDemoDatabase(),
      productId,
      variantId,
    );

    if (items.length > 0) {
      const maxAvailable = await resolveMaxAvailable(productId, variantId);
      return convertModelToCartItemData(items[0], maxAvailable);
    }
    return null;
  } catch (err) {
    console.error('[getDemoCartItemByProductId] Error:', err);
    return null;
  }
}
